import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  Input,
  Spinner,
} from "reactstrap";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  saveHireStatus,
  fetchCommonHireStatusOptions,
  fetchHireStatusIncentiveStatusOptions,
} from "model/CandidateModel";
import {
  mapCommonDropdownRecords,
  HIRE_CLIENT_PAID_OPTIONS,
} from "helpers/hireStatusInlineHelpers";

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const INITIAL_FORM = {
  id: "",
  candidateAndSource: "",
  clientAndRecruiter: "",
  dateHired: "",
  incentiveStatus: "",
  clientPaid: "",
  status: "",
  comments: "",
  /** "true" | "false" | "" when null / unknown */
  updateSent: "",
};

const strOrEmpty = (v) => (v === null || v === undefined ? "" : String(v).trim());

const normalizeUpdateSent = (v) => {
  if (v === true || v === "true" || v === 1 || v === "1") return "true";
  if (v === false || v === "false" || v === 0 || v === "0") return "false";
  return "";
};

const buildFormFromRow = (row = {}) => {
  const raw = row?._originalHireStatus ?? row;
  const id = raw?.id || raw?._id || "";
  const dateRaw =
    raw?.dateHired ??
    raw?.dateHiredDate ??
    raw?.hiredDate ??
    raw?.hireDate ??
    "";
  let dateHired = "";
  if (dateRaw) {
    const d = dayjs(dateRaw);
    dateHired = d.isValid() ? d.format("YYYY-MM-DD") : String(dateRaw).slice(0, 10);
  }
  const clientPaidRaw = raw?.clientPaid;
  const clientPaidStr =
    clientPaidRaw === null || clientPaidRaw === undefined
      ? ""
      : String(clientPaidRaw).trim().toLowerCase();

  return {
    ...INITIAL_FORM,
    id,
    candidateAndSource: raw?.candidateAndSource ?? "",
    clientAndRecruiter: raw?.clientAndRecruiter ?? "",
    dateHired,
    incentiveStatus:
      raw?.incentiveStatus != null && raw?.incentiveStatus !== ""
        ? String(raw.incentiveStatus).trim()
        : raw?.incentiveStatusId != null && raw?.incentiveStatusId !== ""
          ? String(raw.incentiveStatusId).trim()
          : "",
    clientPaid: clientPaidStr,
    status: strOrEmpty(raw?.status ?? raw?.orderStatus),
    comments: raw?.comments ?? raw?.comment ?? "",
    updateSent: normalizeUpdateSent(raw?.updateSent),
  };
};

const matchOption = (options, value) => {
  const v = String(value ?? "").trim().toLowerCase();
  if (!v) return null;
  const list = Array.isArray(options) ? options : [];
  return (
    list.find((opt) => String(opt?.value ?? "").toLowerCase() === v) ||
    list.find((opt) => String(opt?.label ?? "").toLowerCase() === v) ||
    null
  );
};

/**
 * Hire status add/edit — label layout aligned with job-category-add-edit-modal.
 * Submit: POST `HireStatus`.
 */
const CandidateHireStatusAddEditModal = ({ isOpen, onClose, row = {}, onSuccess }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  const [incentiveTypeOptions, setIncentiveTypeOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const [hireStatusRes, incentiveRes] = await Promise.all([
        fetchCommonHireStatusOptions(),
        fetchHireStatusIncentiveStatusOptions(),
      ]);
      setOrderStatusOptions(mapCommonDropdownRecords(hireStatusRes?.data));
      setIncentiveTypeOptions(mapCommonDropdownRecords(incentiveRes?.data));
    } catch (e) {
      toast.error(e?.message || "Unable to load options");
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadOptions();
    setForm(buildFormFromRow(row));
  }, [isOpen, row, loadOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const closeModal = () => {
    setForm(INITIAL_FORM);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form?.id) {
      toast.error("Record ID not found");
      return;
    }
    setSaving(true);
    try {
      const dateHiredIso = form?.dateHired
        ? dayjs(form.dateHired).startOf("day").toISOString()
        : null;
      const payload = {
        id: form.id,
        dateHired: dateHiredIso,
        incentiveStatus: form.incentiveStatus || null,
        clientPaid: form.clientPaid || null,
        status: form.status || null,
        comments: form.comments?.trim() ? form.comments.trim() : null,
        updateSent:
          form.updateSent === "true"
            ? true
            : form.updateSent === "false"
              ? false
              : null,
      };
      const response = await saveHireStatus(payload);
      if (response?.success || response?.isSuccess || response?.status) {
        toast.success(response?.message || "Hire status updated");
        onSuccess?.({
          id: form.id,
          dateHired: dateHiredIso,
          incentiveStatus: form.incentiveStatus || null,
          clientPaid: form.clientPaid || null,
          status: form.status || null,
          orderStatus: form.status || null,
          comments: form.comments?.trim() ? form.comments.trim() : null,
        });
        closeModal();
      } else {
        toast.error(response?.message || response?.error?.message || "Unable to save");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      size="md"
      centered
      backdrop="static"
      className="modal-full-height"
    >
      <ModalBody
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">Update</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        {optionsLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            {/* <FormGroup>
              <Label>Candidate &amp; Source</Label>
              {form.candidateAndSource ? (
                <div
                  className="form-control-plaintext border rounded px-2 py-2 small bg-light"
                  dangerouslySetInnerHTML={{ __html: String(form.candidateAndSource) }}
                />
              ) : (
                <span className="text-muted">—</span>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Client &amp; Recruiter</Label>
              {form.clientAndRecruiter ? (
                <div
                  className="form-control-plaintext border rounded px-2 py-2 small bg-light"
                  dangerouslySetInnerHTML={{ __html: String(form.clientAndRecruiter) }}
                />
              ) : (
                <span className="text-muted">—</span>
              )}
            </FormGroup> */}

            {/* <FormGroup>
              <Label>Update Sent</Label>
              <div className="text-body">
                {form.updateSent === "true"
                  ? "Yes"
                  : form.updateSent === "false"
                    ? "No"
                    : "—"}
              </div>
            </FormGroup> */}

            <FormGroup>
              <Label>Date Hired</Label>
              <Input
                type="date"
                name="dateHired"
                value={form.dateHired || ""}
                onChange={handleChange}
                placeholder="dd/mm/yyyy"
              />
            </FormGroup>

            <FormGroup>
              <Label>Incentive Type</Label>
              <Select
                options={incentiveTypeOptions}
                className="select2-selection"
                placeholder="Select incentive type"
                isClearable
                value={matchOption(incentiveTypeOptions, form.incentiveStatus)}
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, incentiveStatus: opt?.value ?? "" }))
                }
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={selectZIndexStyles}
              />
            </FormGroup>

            <FormGroup>
              <Label>Client Paid</Label>
              <Select
                options={HIRE_CLIENT_PAID_OPTIONS}
                className="select2-selection"
                placeholder="Select client paid"
                isClearable
                value={matchOption(HIRE_CLIENT_PAID_OPTIONS, form.clientPaid)}
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, clientPaid: opt?.value ?? "" }))
                }
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={selectZIndexStyles}
              />
            </FormGroup>

            <FormGroup>
              <Label>Status</Label>
              <Select
                options={orderStatusOptions}
                className="select2-selection"
                placeholder="Select status"
                isClearable
                value={matchOption(orderStatusOptions, form.status)}
                onChange={(opt) => setForm((prev) => ({ ...prev, status: opt?.value ?? "" }))}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={selectZIndexStyles}
              />
            </FormGroup>

            <FormGroup>
              <Label>Comments</Label>
              <Input
                type="text"
                name="comments"
                value={form.comments || ""}
                onChange={handleChange}
                placeholder="Comments"
              />
            </FormGroup>

            <div className="text-end mt-3">
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Update"}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

export default CandidateHireStatusAddEditModal;
